import React, { useEffect, useMemo, useState } from "react";
import { Search, ShieldCheck } from "lucide-react";
import { open_api_with_cookies } from "../../../Utils/network";
import "../shared/AdminData.css";

const PAGE_SIZE = 20;

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (purchase) => {
  const amount = purchase.total_amount ?? purchase.amount;
  if (amount === null || amount === undefined) return "—";
  const currency = purchase.currency || "INR";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${currency} ${Number(amount).toLocaleString("en-IN")}`;
  }
};

const phoneLabel = (user) => {
  if (!user?.phone) return "No phone";
  return `${user.isd_code || ""} ${user.phone}`.trim();
};

const pageNumbers = (page, totalPages) => {
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  return Array.from(
    { length: Math.min(5, totalPages) },
    (_, index) => start + index
  );
};

const paymentStatusClass = (status) => {
  if (status === 4) return "status-positive";
  if (status === 1 || status === 3) return "status-warning";
  if (status === 2 || status === 5 || status === 6) return "status-negative";
  return "status-neutral";
};

const sameAccount = (purchase) =>
  purchase.purchaser?.id && purchase.purchaser.id === purchase.account?.id;

const AdminPurchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [purchaseType, setPurchaseType] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [source, setSource] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let isCurrent = true;
    const fetchPurchases = async () => {
      setIsLoading(true);
      setError("");
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (purchaseType) params.set("type", purchaseType);
      if (paymentStatus) params.set("payment_status", paymentStatus);
      if (source) params.set("source", source);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);

      try {
        const response = await open_api_with_cookies.get(
          `/dashboard/admin/purchases/?${params.toString()}`
        );
        if (!isCurrent) return;
        setPurchases(response.data.data || []);
        setSummary(response.data.summary || {});
        setTotalPages(response.data.max_pages || 1);
        setTotal(response.data.total_count || 0);
      } catch (requestError) {
        if (!isCurrent) return;
        console.error("Error fetching admin purchases:", requestError);
        setError("Purchases could not be loaded. Please try again.");
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    fetchPurchases();
    return () => {
      isCurrent = false;
    };
  }, [page, debouncedSearch, purchaseType, paymentStatus, source, startDate, endDate]);

  const hasFilters = useMemo(
    () => Boolean(search || purchaseType || paymentStatus || source || startDate || endDate),
    [search, purchaseType, paymentStatus, source, startDate, endDate]
  );

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setPurchaseType("");
    setPaymentStatus("");
    setSource("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const summaryCards = [
    ["All purchases", summary.total_purchases || 0, "Workshop and session payment records"],
    ["Paid", summary.paid || 0, "Successfully paid records"],
    ["Workshops", summary.workshops || 0, "Workshop purchases"],
    ["Sessions", summary.sessions || 0, "1-on-1 session purchases"],
  ];

  return (
    <div className="admin-data-page">
      <div className="admin-data-heading">
        <div>
          <h1>Purchases</h1>
          <p>Consolidated workshop and session purchases with buyer, account, and expert context.</p>
        </div>
        <span className="admin-only-label">
          <ShieldCheck /> Admin only
        </span>
      </div>

      <div className="admin-summary-grid">
        {summaryCards.map(([label, value, note]) => (
          <div className="admin-summary-card" key={label}>
            <div className="admin-summary-label">{label}</div>
            <div className="admin-summary-value">{Number(value).toLocaleString()}</div>
            <div className="admin-summary-note">{note}</div>
          </div>
        ))}
      </div>

      <div className="admin-data-toolbar">
        <label className="admin-data-search">
          <Search aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search buyer, product, expert, transaction, or order"
            aria-label="Search purchases"
          />
        </label>
        <select
          className="admin-filter-select"
          value={purchaseType}
          onChange={(event) => {
            setPurchaseType(event.target.value);
            setPage(1);
          }}
          aria-label="Purchase type"
        >
          <option value="">All types</option>
          <option value="workshop">Workshops</option>
          <option value="session">Sessions</option>
        </select>
        <select
          className="admin-filter-select"
          value={paymentStatus}
          onChange={(event) => {
            setPaymentStatus(event.target.value);
            setPage(1);
          }}
          aria-label="Payment status"
        >
          <option value="">All payment statuses</option>
          <option value="4">Paid</option>
          <option value="1">Pending</option>
          <option value="3">Partially paid</option>
          <option value="5">Failed</option>
          <option value="2">Cancelled</option>
          <option value="6">Refund</option>
        </select>
        <select
          className="admin-filter-select"
          value={source}
          onChange={(event) => {
            setSource(event.target.value);
            setPage(1);
          }}
          aria-label="Purchase source"
        >
          <option value="">All sources</option>
          <option value="1">Website</option>
          <option value="2">App</option>
        </select>
        <input
          className="admin-date-input"
          type="date"
          value={startDate}
          onChange={(event) => {
            setStartDate(event.target.value);
            setPage(1);
          }}
          aria-label="Purchases from date"
        />
        <input
          className="admin-date-input"
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={(event) => {
            setEndDate(event.target.value);
            setPage(1);
          }}
          aria-label="Purchases to date"
        />
        {hasFilters && (
          <button className="admin-clear-filters" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      <div className="admin-data-card">
        {isLoading ? (
          <div className="admin-table-loading">Loading purchases…</div>
        ) : error ? (
          <div className="admin-error-state">{error}</div>
        ) : purchases.length === 0 ? (
          <div className="admin-empty-state">No purchases match these filters.</div>
        ) : (
          <div className="admin-table-scroll">
            <table className="admin-data-table admin-purchases-table">
              <colgroup>
                <col style={{ width: "125px" }} />
                <col style={{ width: "210px" }} />
                <col style={{ width: "210px" }} />
                <col style={{ width: "210px" }} />
                <col style={{ width: "180px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "105px" }} />
                <col style={{ width: "125px" }} />
                <col style={{ width: "170px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Purchase</th>
                  <th>Purchased for</th>
                  <th>Payment account</th>
                  <th>Product</th>
                  <th>Expert / sold by</th>
                  <th>Amount</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Transaction</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => (
                  <tr key={purchase.id}>
                    <td>
                      <div className="admin-primary-text">#{purchase.purchase_id}</div>
                      <div className="admin-secondary-text">Payment #{purchase.id}</div>
                      <div className="admin-secondary-text">{formatDateTime(purchase.created)}</div>
                    </td>
                    <td>
                      <div className="admin-primary-text">{purchase.purchaser?.name || "Unknown user"}</div>
                      <div className="admin-secondary-text">{purchase.purchaser?.email || "No email"}</div>
                      <div className="admin-secondary-text">{phoneLabel(purchase.purchaser)}</div>
                    </td>
                    <td>
                      <div className="admin-primary-text">{purchase.account?.name || "Unknown account"}</div>
                      <div className="admin-secondary-text">{purchase.account?.email || "No email"}</div>
                      <div className="admin-secondary-text">
                        {sameAccount(purchase) ? "Same as purchaser" : "Different paying account"}
                      </div>
                    </td>
                    <td>
                      <span className="admin-type-pill">
                        {purchase.purchase_type === "workshop" ? "Workshop" : "Session"}
                      </span>
                      <div className="admin-primary-text" title={purchase.product?.title}>
                        {purchase.product?.title || "Deleted product"}
                      </div>
                      <div className="admin-secondary-text">
                        Product #{purchase.product?.id || "—"}
                        {purchase.workshop_batch?.name ? ` · ${purchase.workshop_batch.name}` : ""}
                      </div>
                    </td>
                    <td>
                      <div className="admin-experts-list">
                        {(purchase.experts || []).length > 0 ? (
                          purchase.experts.map((expert) => (
                            <span className="admin-expert-name" key={expert.id} title={expert.email || expert.name}>
                              {expert.name}
                            </span>
                          ))
                        ) : (
                          <span className="admin-secondary-text">No expert linked</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="admin-primary-text">{formatAmount(purchase)}</div>
                      {purchase.coupon_code && (
                        <div className="admin-secondary-text">Coupon: {purchase.coupon_code}</div>
                      )}
                    </td>
                    <td>
                      <span className="admin-type-pill">{purchase.purchased_from || "Unknown"}</span>
                      {purchase.is_apple && <div className="admin-secondary-text">Apple payment</div>}
                    </td>
                    <td>
                      <span className={`admin-status-pill ${paymentStatusClass(purchase.payment_status)}`}>
                        {purchase.payment_status_display || "Unknown"}
                      </span>
                      <div className="admin-secondary-text">{purchase.purchase_status || purchase.purchase_payment_status || "—"}</div>
                    </td>
                    <td>
                      <div className="admin-mono-text">{purchase.transaction_id || "No transaction ID"}</div>
                      {purchase.order_id && <div className="admin-secondary-text">Order: {purchase.order_id}</div>}
                      {purchase.is_bypassed && <div className="admin-secondary-text">Bypassed</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && !error && total > 0 && (
          <div className="admin-pagination">
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <div className="admin-pagination-controls">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                Previous
              </button>
              {pageNumbers(page, totalPages).map((pageNumber) => (
                <button
                  key={pageNumber}
                  className={pageNumber === page ? "active" : ""}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPurchases;
